import { useSeo } from "@/lib/seo";
import { trackPage } from "@/lib/track";
import { DEVICES, LEGACY_DEVICES } from "@/lib/devices";
import { useEffect } from "react";
import "@/styles/home.css";

const SITE = "https://99tests.com";
const PREMIUM = new Set(["galaxy-s23", "oneplus-12"]);

const GROUPS = [
  ["Budget & mid-range Android", DEVICES.filter((d) => d.brand !== "Apple" && !PREMIUM.has(d.slug))],
  ["Premium Android", DEVICES.filter((d) => PREMIUM.has(d.slug))],
  ["iPhones", DEVICES.filter((d) => d.brand === "Apple")],
];

function Header() {
  return (
    <header className="site-header"><div className="wrap">
      <a className="logo" href="/"><i></i>99tests</a>
      <nav>
        <a href="/#how">How it works</a>
        <a href="/devices" data-testid="nav-devices">Devices</a>
        <a href="/tools" data-testid="nav-tools">Free tools</a>
        <a href="/pricing">Pricing</a>
        <a href="https://blog.99tests.com/" data-testid="nav-blog">Blog</a>
        <a className="btn sm blue" href="/pricing" data-testid="devices-start-job-btn">Start a job</a>
      </nav>
    </div></header>
  );
}

function Footer() {
  return (
    <footer className="site-footer"><div className="wrap">
      <div>© 2026 99tests · Bengaluru<br /><span className="small">Not test automation. Not a certification or an audit under any standard.</span></div>
      <div>
        <a href="/crowdsourced-testing">Crowdsourced testing</a>
        <a href="/mobile-app-testing-india">Mobile app testing in India</a>
        <a href="/test-app-on" data-testid="footer-phones">Phones our testers own</a>
        <a href="/pricing">Pricing</a>
        <a href="/sample-report/">Sample report</a>
      </div>
      <div>
        <a href="/">Home</a>
        <a href="/join">Become a tester</a>
        <a href="/mcp-server" data-testid="footer-mcp">For AI agents (MCP)</a>
        <a href="/terms">Client terms</a>
        <a href="mailto:hello@99tests.com">hello@99tests.com</a>
      </div>
    </div></footer>
  );
}

export default function Devices() {
  const path = "/devices";
  const jsonld = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "Devices we test on", item: SITE + path },
      ] },
      { "@type": "ItemList", itemListElement: [...DEVICES, ...LEGACY_DEVICES].map((d, i) => ({
        "@type": "ListItem", position: i + 1, name: `Test your app on ${d.model}`, url: SITE + `/test-app-on/${d.slug}`,
      })) },
    ],
  };
  useSeo({
    title: "Devices we test on — real phones in India",
    description: "Run your test cases on 20+ real phones Indians own — Redmi, Realme, Galaxy, Vivo, Oppo, Poco, OnePlus and iPhone. Verified 3-day report.",
    path,
    jsonld,
  });
  useEffect(() => { trackPage(path); }, []);

  return (
    <>
      <Header />
      <main data-testid="devices-hub">
        <section className="hero">
          <div className="wrap">
            <div>
              <div className="ref"><b>Real devices</b><span className="dot"></span><b>Indian networks</b><span className="dot"></span><span>Android &amp; iPhone</span></div>
              <h1>Pick the phone your <em>users actually own.</em></h1>
              <p className="lede">We run your test cases on the exact budget Androids and iPhones Indians carry — each with its own skin quirks worth testing for. Choose a device to see what breaks on it, then start a job from ₹9,999.</p>
              <p><a href="/test-app-on" data-testid="devices-phones-link">See all 58 phones and tablets our testers own →</a></p>
              <div className="actions">
                <a className="btn blue" href="/pricing" data-testid="devices-packages-btn">See packages</a>
                <a className="btn ghost" href="/sample-report/">Read a sample report</a>
              </div>
            </div>
          </div>
        </section>

        {GROUPS.map(([title, list]) => (
          <section className="band" key={title}>
            <div className="wrap">
              <div className="section-head"><span className="eyebrow">{list.length} {list.length === 1 ? "device" : "devices"}</span><h2>{title}.</h2></div>
              <div data-testid={`devices-group`} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
                {list.map((d) => (
                  <a key={d.slug} href={`/test-app-on/${d.slug}`} data-testid={`device-card-${d.slug}`}
                    className="panel-soft" style={{ display: "block", padding: "16px 18px", textDecoration: "none", color: "inherit", border: "1px solid var(--rule)", borderRadius: "var(--r-l)" }}>
                    <h4 style={{ margin: "0 0 6px", fontSize: 18 }}>{d.model}</h4>
                    <p className="small" style={{ margin: 0, color: "var(--muted)" }}>{d.os} · {d.skin}</p>
                    <p className="small" style={{ margin: "8px 0 0", fontWeight: 600 }}>From ₹9,999 · {d.price}</p>
                  </a>
                ))}
              </div>
            </div>
          </section>
        ))}

        <section className="band" key="older-devices">
          <div className="wrap">
            <div className="section-head"><span className="eyebrow">{LEGACY_DEVICES.length} devices</span><h2>Older devices our testers still own.</h2></div>
            <p className="small" style={{ margin: "0 0 14px", color: "var(--muted)", maxWidth: 680 }}>Legacy phones and tablets a big slice of Indian users never upgraded from — old OS builds, dated WebViews and skins that surface bugs newer test devices hide.</p>
            <div data-testid="devices-group-older" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 14 }}>
              {LEGACY_DEVICES.map((d) => (
                <a key={d.slug} href={`/test-app-on/${d.slug}`} data-testid={`device-card-${d.slug}`}
                  className="panel-soft" style={{ display: "block", padding: "16px 18px", textDecoration: "none", color: "inherit", border: "1px solid var(--rule)", borderRadius: "var(--r-l)" }}>
                  <h4 style={{ margin: "0 0 6px", fontSize: 18 }}>{d.model}</h4>
                  <p className="small" style={{ margin: 0, color: "var(--muted)" }}>{d.soft} · {d.kind}</p>
                  <p className="small" style={{ margin: "8px 0 0", fontWeight: 600 }}>From ₹9,999 · Older device ({d.era})</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section style={{ borderTop: 0 }}>
          <div className="wrap">
            <div className="cta">
              <div>
                <h2>Don't see your exact phone?</h2>
                <p>We match testers by brand and minimum OS across 20,000 people in Indian cities. Tell us the target at checkout and we'll match the owner.</p>
              </div>
              <a className="btn" href="/pricing">See packages</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
